import {
    CenteredWrapper,
    CloseButton,
    DropdownWithLoader,
    ErrorMessage,
    ErrorText,
    FormField,
    FormGrid,
    FormRow,
    FormSection,
    FormTitleWrapper,
    InputSuffix,
    InputWithSuffix,
    StepButton,
    StepButtonGroup,
    SuccessMessage,
    SuccessText,
    SyncRow,
} from "../custom-styles/FormSection.styles";
import React, { useMemo } from "react";
import Text, { VARIANTS } from "shared/components/data-display/text/Text";
import { format, isAfter, parseISO, startOfDay } from "date-fns";

import COLORS from "shared/config/colors";
import CheckIcon from "@material-ui/icons/Check";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import DropdownSingleSelectWithoutSearch from "shared/components/inputs/dropdown/DropdownSingleSelectWithoutSearch";
import EditIcon from "@material-ui/icons/Edit";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import FormSectionStatus from "./FormSectionStatus";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import PrimaryButton from "shared/components/inputs/button/PrimaryButton";
import PropTypes from "prop-types";
import Sync from "shared/components/feedback/loading/Sync";
import TextInputField from "shared/components/inputs/input/TextInput";

function FormSectionComponent({
    selectedJob,
    onJobSelect,
    assignmentsData,
    departmentAssignmentsData,
    hours,
    setHours,
    selectedDate,
    handleSubmit,
    updateHours,
    showSuccess,
    showError,
    errorMessage,
    onDismissError,
    isTimesheetSubmitted,
    isTimesheetSubmittedLoading,
    onRefresh,
    isLoading,
}) {
    // Transform assignments to dropdown options grouped by client
    const jobOptions = useMemo(() => {
        if (!assignmentsData?.data) {
            return [];
        }

        // Group by client
        const clientGroups = {};
        assignmentsData.data.forEach((assignment) => {
            const clientId = assignment.ClientId;
            if (!clientGroups[clientId]) {
                clientGroups[clientId] = {
                    clientName: assignment.ClientName,
                    projects: [],
                };
            }
            clientGroups[clientId].projects.push(assignment);
        });

        const options = [];

        // Grouped by client
        Object.keys(clientGroups).forEach((clientId) => {
            const group = clientGroups[clientId];
            options.push({
                value: `client_${clientId}`,
                label: group.clientName,
                isTitle: true,
            });
            group.projects.forEach((project) => {
                options.push({
                    value: project.ProjectId,
                    label: project.ProjectName,
                    projectData: project,
                });
            });
        });

        // Non-client-related activities (department assignments)
        const deptProjects = departmentAssignmentsData?.data;
        if (deptProjects?.length > 0) {
            options.push({
                value: "non_client_header",
                label: "Non-Client-Related Activities",
                isTitle: true,
            });
            deptProjects.forEach((project) => {
                options.push({
                    value: project.ProjectId,
                    label: project.ProjectName,
                    projectData: project,
                });
            });
        }

        return options;
    }, [assignmentsData?.data, departmentAssignmentsData?.data]);

    // Validate selected date against assignment/project end dates
    const dateValidationError = useMemo(() => {
        if (!selectedJob) return null;

        // Check in regular assignments first
        const assignment = assignmentsData?.data?.find(
            (a) => a.ProjectId === selectedJob
        );
        if (assignment) {
            const { AssignmentEndDate, ProjectEndDate } = assignment;

            if (
                AssignmentEndDate &&
                isAfter(
                    startOfDay(selectedDate),
                    startOfDay(parseISO(AssignmentEndDate))
                )
            ) {
                return `Your assignment for this project ended on ${format(parseISO(AssignmentEndDate), "MMM d, yyyy")}. Please select an earlier date.`;
            }

            if (
                ProjectEndDate &&
                isAfter(
                    startOfDay(selectedDate),
                    startOfDay(parseISO(ProjectEndDate))
                )
            ) {
                return `This project ended on ${format(parseISO(ProjectEndDate), "MMM d, yyyy")}. Please select an earlier date.`;
            }

            return null;
        }

        // Check in department assignments
        const deptAssignment = departmentAssignmentsData?.data?.find(
            (a) => a.ProjectId === selectedJob
        );
        if (
            deptAssignment?.ProjectEndDate &&
            isAfter(
                startOfDay(selectedDate),
                startOfDay(parseISO(deptAssignment.ProjectEndDate))
            )
        ) {
            return `This project ended on ${format(parseISO(deptAssignment.ProjectEndDate), "MMM d, yyyy")}. Please select an earlier date.`;
        }

        return null;
    }, [
        selectedJob,
        selectedDate,
        assignmentsData?.data,
        departmentAssignmentsData?.data,
    ]);

    // Handle job selection change
    const handleJobChange = (value) => {
        // Find the selected option to get the clean job name
        const selectedOption = jobOptions.find((opt) => opt.value === value);

        // Prevent selection of titles (they should not be selectable)
        if (selectedOption?.isTitle) {
            console.warn("Section titles are not selectable");
            return; // Don't process if a title was selected
        }

        // Notify parent with jobId
        onJobSelect(value);
    };

    // Hours input validation
    const handleHoursChange = (e) => {
        const value = e.target.value;
        // Allow empty or valid number input
        if (value === "" || value === "0" || value === "0.") {
            setHours(value);
            return;
        }

        const numValue = Number.parseFloat(value);
        // Only allow valid numbers within range
        if (!Number.isNaN(numValue) && numValue >= 0 && numValue <= 24) {
            setHours(value);
        }
    };

    const handleHoursBlur = (e) => {
        const value = e.target.value;
        if (value === "" || value === "0" || value === "0.") {
            setHours("");
            return;
        }

        let numValue = Number.parseFloat(value);

        // Clamp to valid range
        if (numValue < 0.25) {
            numValue = 0.25;
        } else if (numValue > 24) {
            numValue = 24;
        }

        // Round to nearest 0.25
        numValue = Math.round(numValue * 4) / 4;

        setHours(numValue.toString());
    };

    const handleIncrement = () => {
        const current = Number.parseFloat(hours) || 0;
        const next = Math.min(24, Math.round((current + 0.5) * 4) / 4);
        setHours(next.toString());
    };

    const handleDecrement = () => {
        const current = Number.parseFloat(hours) || 0;
        const next = Math.max(0, Math.round((current - 0.5) * 4) / 4);
        setHours(next === 0 ? "" : next.toString());
    };

    if (isTimesheetSubmittedLoading || isTimesheetSubmitted) {
        return (
            <FormSectionStatus
                isTimesheetSubmittedLoading={isTimesheetSubmittedLoading}
                isTimesheetSubmitted={isTimesheetSubmitted}
            />
        );
    }

    return (
        <FormSection>
            <FormTitleWrapper>
                <EditIcon />
                <Text variant={VARIANTS.Headline_4} color={COLORS.Black}>
                    Time Entry
                </Text>
            </FormTitleWrapper>

            {/* Success Message */}
            {showSuccess && (
                <SuccessMessage>
                    <CheckIcon />
                    <SuccessText>Time entry saved successfully!</SuccessText>
                </SuccessMessage>
            )}

            {/* Date validation error (auto-clears on job/date change) */}
            {dateValidationError && (
                <ErrorMessage>
                    <ErrorOutlineIcon />
                    <ErrorText>{dateValidationError}</ErrorText>
                </ErrorMessage>
            )}

            {/* API Error Message */}
            {showError && (
                <ErrorMessage>
                    <ErrorOutlineIcon />
                    <ErrorText>
                        {errorMessage ||
                            "Failed to save time entry. Please try again."}
                    </ErrorText>
                    <CloseButton onClick={onDismissError}>
                        <CloseIcon />
                    </CloseButton>
                </ErrorMessage>
            )}
            <FormGrid>
                <FormField>
                    <Text
                        variant={VARIANTS.Descriptive_1_1}
                        color={COLORS.Gray_1}
                    >
                        Projects
                    </Text>

                    <DropdownWithLoader>
                        <DropdownSingleSelectWithoutSearch
                            value={selectedJob}
                            onChange={handleJobChange}
                            options={jobOptions}
                            isSmallSize={true}
                            isSearchInputSmall={true}
                            placeholder={
                                assignmentsData?.isFetching
                                    ? "Loading ..."
                                    : "Select a project"
                            }
                            disabled={
                                assignmentsData?.isFetching ||
                                !jobOptions.length
                            }
                        />
                        {assignmentsData?.isFetching && (
                            <CircularProgress
                                size={16}
                                thickness={4}
                                style={{ color: "#9ca3af" }}
                            />
                        )}
                    </DropdownWithLoader>
                </FormField>

                <FormRow>
                    <FormField>
                        <Text color="Gray_1" variant="Descriptive_1_1">
                            Hours
                        </Text>
                        <InputWithSuffix>
                            <TextInputField
                                type="number"
                                value={hours}
                                placeholder="0.00"
                                onChange={handleHoursChange}
                                onBlur={handleHoursBlur}
                                isSmallSize={true}
                            />
                            <StepButtonGroup>
                                <StepButton
                                    type="button"
                                    onClick={handleIncrement}
                                    disabled={Number.parseFloat(hours) >= 24}
                                    aria-label="Increase hours"
                                >
                                    <KeyboardArrowUpIcon />
                                </StepButton>
                                <StepButton
                                    type="button"
                                    onClick={handleDecrement}
                                    disabled={
                                        !Number.parseFloat(hours) ||
                                        Number.parseFloat(hours) <= 0.5
                                    }
                                    aria-label="Decrease hours"
                                >
                                    <KeyboardArrowDownIcon />
                                </StepButton>
                            </StepButtonGroup>
                            <InputSuffix>hrs</InputSuffix>
                        </InputWithSuffix>
                    </FormField>
                    <CenteredWrapper>
                        <PrimaryButton
                            id="add-hours-button-dts"
                            label={
                                updateHours.isLoading
                                    ? "Saving..."
                                    : "Add Hours"
                            }
                            onClick={handleSubmit}
                            isMedium
                            disabled={
                                updateHours.isLoading ||
                                !selectedJob ||
                                !Number.parseFloat(hours) ||
                                assignmentsData?.isFetching ||
                                !!dateValidationError
                            }
                        />
                    </CenteredWrapper>
                </FormRow>
            </FormGrid>

            <SyncRow>
                <Sync onRefresh={onRefresh} isLoading={isLoading} />
            </SyncRow>
        </FormSection>
    );
}

FormSectionComponent.propTypes = {
    selectedJob: PropTypes.string,
    onJobSelect: PropTypes.func,
    assignmentsData: PropTypes.object,
    departmentAssignmentsData: PropTypes.object,
    hours: PropTypes.string,
    setHours: PropTypes.func,
    selectedDate: PropTypes.instanceOf(Date),
    handleSubmit: PropTypes.func,
    updateHours: PropTypes.object,
    showSuccess: PropTypes.bool,
    showError: PropTypes.bool,
    errorMessage: PropTypes.string,
    onDismissError: PropTypes.func,
    isTimesheetSubmitted: PropTypes.bool,
    isTimesheetSubmittedLoading: PropTypes.bool,
    onRefresh: PropTypes.func,
    isLoading: PropTypes.bool,
};

export default FormSectionComponent;
